import { useApiRequest } from '@/shared/api/useApiRequest'
import type { ApiListResponse, ApiResponse } from '@/types/api'
import type {
  CreateSupplierData,
  Supplier,
  SupplierStats,
  SupplierStatus,
  UpdateSupplierData,
} from '@/types/supplier'

export interface SupplierSearchFilters {
  status?: SupplierStatus[]
  rating?: number
  city?: string
  hasProducts?: boolean
  isFeatured?: boolean
  search?: string
}

export interface SupplierSortOptions {
  by: 'rating' | 'name' | 'createdAt' | 'productCount'
  order: 'asc' | 'desc'
}

export interface SupplierPaginationParams {
  page?: number
  limit?: number
}

export function normalizeSupplierForTests(raw: unknown): Supplier {
  const r = raw as Record<string, unknown>
  const bd = (r['business_details'] ?? {}) as Record<string, unknown>
  const ci = (r['contact_info'] ?? {}) as Record<string, unknown>

  const businessDetails: Supplier['businessDetails'] = {
    inn: String(bd['inn'] ?? ''),
    legalName: String(bd['legal_name'] ?? ''),
  }
  if (bd['kpp']) businessDetails.kpp = String(bd['kpp'])
  if (bd['ogrn']) businessDetails.ogrn = String(bd['ogrn'])
  if (bd['legal_address']) businessDetails.legalAddress = String(bd['legal_address'])

  const contactInfo: Supplier['contactInfo'] = {
    email: String(ci['email'] ?? ''),
  }
  if (ci['phone']) contactInfo.phone = String(ci['phone'])
  if (ci['address']) contactInfo.address = String(ci['address'])
  if (ci['website']) contactInfo.website = String(ci['website'])
  if (ci['contact_person']) contactInfo.contactPerson = String(ci['contact_person'])
  if (ci['contact_position']) contactInfo.contactPosition = String(ci['contact_position'])

  const supplier: Supplier = {
    id: String(r['id']),
    businessDetails,
    contactInfo,
    status: String(r['status'] ?? 'pending') as Supplier['status'],
    rating: Number(r['rating'] ?? 0),
    reviewCount: Number(r['review_count'] ?? 0),
    isActive: Boolean(r['is_active']),
    isFeatured: Boolean(r['is_featured']),
    createdAt: String(r['created_at'] ?? ''),
    activeProductCount: Number(r['active_product_count'] ?? 0),
    totalProductCount: Number(r['total_product_count'] ?? 0),
  }
  if (r['description']) supplier.description = String(r['description'])
  if (r['updated_at']) supplier.updatedAt = String(r['updated_at'])

  return supplier
}

function normalizeSupplier(raw: unknown): Supplier {
  return normalizeSupplierForTests(raw)
}

/**
 * API client для работы с поставщиками
 * Интегрируется с backend Supplier endpoints
 */
export function useSupplierAPI() {
  const { makeRequest } = useApiRequest()

  /**
   * Получение списка поставщиков с фильтрами
   */
  const getSuppliers = async (
    filters: SupplierSearchFilters = {},
    sort: SupplierSortOptions = { by: 'rating', order: 'desc' },
    pagination: SupplierPaginationParams = { page: 1, limit: 20 },
    scope: 'public' | 'admin' = 'public',
  ): Promise<ApiListResponse<Supplier>> => {
    const params: Record<string, string | number | boolean | string[] | undefined> = {
      ...filters,
      sort_by: sort.by,
      sort_order: sort.order,
    }

    if (pagination.page) params['page'] = pagination.page
    if (pagination.limit) params['limit'] = pagination.limit

    const raw = await makeRequest<{
      success?: boolean
      data?: {
        suppliers?: Supplier[]
        items?: Supplier[]
        pagination?: ApiListResponse<Supplier>['data']['pagination']
      }
      suppliers?: Supplier[]
      items?: Supplier[]
      pagination?: ApiListResponse<Supplier>['data']['pagination']
    }>(
      // Публичный справочник отдаёт только одобренных, админке нужны все статусы
      scope === 'admin' ? '/api/admin/suppliers' : '/api/suppliers',
      {
        method: 'GET',
      },
      Object.fromEntries(Object.entries(params).filter(([_, v]) => v !== undefined)) as Record<
        string,
        string | number | boolean | string[]
      >,
    )

    const rawItems: unknown[] =
      ((raw as Record<string, unknown>)?.['suppliers'] as unknown[]) ??
      ((raw as Record<string, unknown>)?.['items'] as unknown[]) ??
      []
    const responsePagination = (raw as Record<string, unknown>)?.['pagination']
    const items: Supplier[] = rawItems.map(normalizeSupplier)

    return { data: { items, pagination: responsePagination } } as ApiListResponse<Supplier>
  }

  /**
   * Получение поставщика по ID
   */
  const getSupplier = async (supplierId: string): Promise<Supplier> => {
    const response = await makeRequest<ApiResponse<Supplier>>(`/api/suppliers/${supplierId}`, {
      method: 'GET',
    })
    return normalizeSupplier(response.data)
  }

  /**
   * Создание нового поставщика
   */
  const createSupplier = async (data: CreateSupplierData): Promise<Supplier> => {
    const response = await makeRequest<ApiResponse<Supplier>>('/api/suppliers', {
      method: 'POST',
      body: JSON.stringify(data),
    })
    return normalizeSupplier(response.data)
  }

  /**
   * Обновление поставщика
   */
  const updateSupplier = async (
    supplierId: string,
    data: UpdateSupplierData,
  ): Promise<Supplier> => {
    const response = await makeRequest<ApiResponse<Supplier>>(`/api/suppliers/${supplierId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
    return normalizeSupplier(response.data)
  }

  /**
   * Удаление поставщика
   */
  const deleteSupplier = async (supplierId: string): Promise<void> => {
    await makeRequest(`/api/suppliers/${supplierId}`, {
      method: 'DELETE',
    })
  }

  /**
   * Одобрение поставщика (admin)
   */
  const approveSupplier = async (supplierId: string, notes?: string): Promise<Supplier> => {
    const response = await makeRequest<ApiResponse<Supplier>>(
      `/api/admin/suppliers/${supplierId}/approve`,
      { method: 'POST', body: JSON.stringify({ notes }) },
    )
    return normalizeSupplier(response.data ?? response)
  }

  /**
   * Отклонение поставщика (admin)
   */
  const rejectSupplier = async (supplierId: string, reason: string): Promise<Supplier> => {
    const response = await makeRequest<ApiResponse<Supplier>>(
      `/api/admin/suppliers/${supplierId}/reject`,
      { method: 'POST', body: JSON.stringify({ reason }) },
    )
    return normalizeSupplier(response.data ?? response)
  }

  /**
   * Приостановка поставщика (admin)
   */
  const suspendSupplier = async (supplierId: string, reason: string): Promise<Supplier> => {
    const response = await makeRequest<ApiResponse<Supplier>>(
      `/api/admin/suppliers/${supplierId}/suspend`,
      { method: 'POST', body: JSON.stringify({ reason }) },
    )
    return normalizeSupplier(response.data ?? response)
  }

  /**
   * Активация поставщика (admin)
   */
  const activateSupplier = async (supplierId: string): Promise<Supplier> => {
    const response = await makeRequest<ApiResponse<Supplier>>(
      `/api/admin/suppliers/${supplierId}/activate`,
      { method: 'POST' },
    )
    return normalizeSupplier(response.data ?? response)
  }

  /**
   * Получение статистики поставщиков
   */
  const getSupplierStats = async (): Promise<SupplierStats> => {
    return await makeRequest<SupplierStats>('/api/suppliers/stats', {
      method: 'GET',
    })
  }

  /**
   * Получение топ поставщиков
   */
  const getTopSuppliers = async (limit: number = 10): Promise<Supplier[]> => {
    return await makeRequest<Supplier[]>(
      '/api/suppliers/top',
      {
        method: 'GET',
      },
      { limit },
    )
  }

  /**
   * Получение рекомендуемых поставщиков
   */
  const getFeaturedSuppliers = async (limit: number = 10): Promise<Supplier[]> => {
    return await makeRequest<Supplier[]>(
      '/api/suppliers/featured',
      {
        method: 'GET',
      },
      { limit },
    )
  }

  /**
   * Поиск поставщиков
   */
  const searchSuppliers = async (query: string, limit: number = 10): Promise<Supplier[]> => {
    return await makeRequest<Supplier[]>(
      '/api/suppliers/search',
      {
        method: 'GET',
      },
      { q: query, limit },
    )
  }

  /**
   * Массовые операции с поставщиками (admin)
   */
  const bulkAction = async (data: {
    action: 'approve' | 'reject' | 'suspend' | 'activate' | 'delete'
    ids: string[]
    reason?: string
    notes?: string
  }): Promise<{ message: string; count: number }> => {
    return await makeRequest('/api/admin/suppliers/bulk', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Проверка уникальности ИНН (admin)
   */
  const checkInnUniqueness = async (
    inn: string,
    excludeId?: string,
  ): Promise<{ isUnique: boolean }> => {
    return await makeRequest<{ isUnique: boolean }>('/api/admin/suppliers/check-inn', {
      method: 'POST',
      body: JSON.stringify({ inn, exclude_id: excludeId }),
    })
  }

  /**
   * Получение данных для форм админки (справочники)
   */
  const getFormData = async (): Promise<{
    statuses: Array<{ value: SupplierStatus; label: string }>
    cities: string[]
    categories: string[]
  }> => {
    return await makeRequest('/api/admin/suppliers/form-data', {
      method: 'GET',
    })
  }

  /**
   * Список поставщиков для админки
   */
  const getAdminSuppliers = async (params: {
    page?: number
    limit?: number
    search?: string
    status?: SupplierStatus
    rating?: number
    city?: string
  }): Promise<{
    suppliers: Supplier[]
    pagination: {
      page: number
      limit: number
      total: number
      pages: number
    }
    filters: {
      statuses: Array<{ value: SupplierStatus; label: string }>
      cities: string[]
      ratings: number[]
    }
  }> => {
    return await makeRequest(
      '/api/admin/suppliers',
      {
        method: 'GET',
      },
      { ...params },
    )
  }

  return {
    getSuppliers,
    getSupplier,
    createSupplier,
    updateSupplier,
    deleteSupplier,
    approveSupplier,
    rejectSupplier,
    suspendSupplier,
    activateSupplier,
    getSupplierStats,
    getTopSuppliers,
    getFeaturedSuppliers,
    searchSuppliers,
    bulkAction,
    checkInnUniqueness,
    getFormData,
    getAdminSuppliers,
  }
}

export const supplierApi = () => useSupplierAPI()
